import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Table from 'react-bootstrap/Table';
import {
  formatNisAmount,
  formatPercentUsed,
  getBurnProgressVariant,
} from '../../utils/labData';

/**
 * Lab Data budget burn table. A row opens the burn detail for that budget.
 * The edit control is optional and is omitted on the analytics page.
 *
 * @param {Object} props
 * @param {Array} [props.rows]
 * @param {(row: Object) => void} [props.onEdit]
 * @param {string} [props.dateFrom]
 * @param {string} [props.dateTo]
 */
const BudgetLinesTable = ({ rows = [], onEdit, dateFrom, dateTo }) => {
  const navigate = useNavigate();

  const openDetail = row => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('from', dateFrom);
    if (dateTo) params.set('to', dateTo);
    const qs = params.toString();
    navigate(`/lab-data/budgets/${row.id}${qs ? `?${qs}` : ''}`);
  };

  return (
    <div className="glass-panel lab-data-table-card">
      <h3 className="lab-data-section-title">Budget burn</h3>
      {!rows?.length ? (
        <div className="glass-empty">No budget burn data yet.</div>
      ) : (
        <div className="glass-table-wrap">
          <Table hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Number</th>
                <th>Name</th>
                <th>PI</th>
                <th className="text-end">Allocated</th>
                <th className="text-end">Spent</th>
                <th className="text-end">Remaining</th>
                <th style={{ width: 160 }}>% used</th>
                {onEdit ? <th className="text-end" style={{ width: 72 }} /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const pct = Number(row.percent_used ?? 0);
                const clamped = Math.min(100, Math.max(0, pct));
                const numberLabel = row.number || row.code || '—';
                const pi = row.primary_investigator;
                const piLabel = pi
                  ? `${pi.first_name || ''} ${pi.last_name || ''}`.trim() ||
                    pi.username ||
                    '—'
                  : '—';
                return (
                  <tr
                    key={row.id}
                    className="lab-data-row-clickable"
                    onClick={() => openDetail(row)}
                  >
                    <td>{numberLabel}</td>
                    <td>{row.name}</td>
                    <td>{piLabel}</td>
                    <td className="text-end">
                      {formatNisAmount(row.allocated_amount)} {row.currency || ''}
                    </td>
                    <td className="text-end">{formatNisAmount(row.spent_nis)}</td>
                    <td className="text-end">{formatNisAmount(row.remaining_nis)}</td>
                    <td>
                      <div className="small mb-1">{formatPercentUsed(pct)}</div>
                      <ProgressBar
                        className="lab-data-burn-progress"
                        variant={getBurnProgressVariant(pct)}
                        now={clamped}
                      />
                    </td>
                    {onEdit ? (
                      <td className="text-end" onClick={e => e.stopPropagation()}>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          aria-label="Edit"
                          onClick={() => onEdit(row)}
                        >
                          Edit
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default BudgetLinesTable;
